(ns hai.ui.components.chip
  "The one removable-chip pattern: an unfilled capsule — hairline sage outline,
   optional glyph, label, and, when removable, a full-height × cell divided off
   by a border. Purely presentational; tag, link, contact (and any future) chips
   compose it with their own glyph, label element, and remove behaviour."
  (:require
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

(o/defstyled entity-chip :span
  {:display "inline-flex" :align-items "stretch"
   :max-width "100%"
   :border-radius "6px" :overflow "hidden"
   ;; No fill: a row of chips sits on a card without stacking blocks of colour
   ;; on it. The hairline is what makes each one countable at a glance, and it
   ;; matches the "+N" pill the row collapses to.
   :border-width "1px" :border-style "solid" :border-color t/--note-sage-55
   :background "transparent"
   :color t/--card-forest
   :font-size "12px" :font-weight "600" :line-height "1"}
  [:.chip-glyph
   {:display "inline-flex" :align-items "center"
    :padding-left "10px"
    :flex "0 0 auto"}
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  ;; inline-block (not flex) so a label passed as an element — an anchor, a
  ;; tooltip-wrapped span — stays inline content and the ellipsis still applies.
  ;; 3px, not 4px: the 1px outline is part of the height, so this keeps a chip
  ;; the same 20px it was when it was a plain fill — and level with the dashed
  ;; "＋" ghost beside it in an editing row.
  [:.chip-label
   {:display "inline-block"
    :padding "3px 10px"
    :min-width "0"
    :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}]
  [:&.has-glyph [:.chip-label {:padding-left "5px"}]]
  [:.chip-remove
   {:display "grid" :place-items "center"
    :width "22px" :flex "0 0 auto"
    :padding "0"
    :border-top "0" :border-right "0" :border-bottom "0"
    :border-left-width "1px" :border-left-style "solid"
    :border-left-color t/--note-sage-55
    :background "transparent"
    :color t/--card-forest
    :cursor "pointer"
    :font-family "inherit" :font-size "15px" :line-height "1"
    :transition "background 120ms ease"}
   ;; Hover fills the × cell with what the whole chip used to be filled with.
   [:&:hover {:background t/--card-sage-light}]]
  ;; `:variant :warning` — for a chip whose subject is time-sensitive, so it
  ;; doesn't read as the same kind of thing as a neutral sage one. Amber, not
  ;; red: a state to notice, not an error. 5.45:1 on the card surface. The one
  ;; chip that keeps a fill: filled-against-outlined is what sets it apart from
  ;; the row now. Its outline goes transparent rather than away, so it stays
  ;; exactly as tall as the chips beside it.
  [:&.warning
   {:background t/--card-warning-12
    :border-color "transparent"
    :color t/--card-warning-text}
   [:.chip-remove
    {:border-left-color "rgba(122,85,0,0.20)"
     :color t/--card-warning-text}
    [:&:hover {:background "rgba(176,122,0,0.18)"}]]]
  ([{:keys [glyph label remove-label on-remove variant]}]
   [:<>
    {:class (cond-> []
              glyph   (conj "has-glyph")
              variant (conj (name variant)))}
    (when glyph [:span.chip-glyph glyph])
    [:span.chip-label label]
    (when on-remove
      [:button.chip-remove
       {:type "button" :aria-label remove-label
        ;; Don't take focus on click — losing it from the host (e.g. a note's
        ;; textarea) would end editing as the chip unmounts.
        :on-mouse-down #(.preventDefault %)
        :on-click (fn [e] (.stopPropagation e) (on-remove))}
       "×"])]))
